import React from 'react';

import { Box } from '@opendoor/atoms/box';

export interface MethodBadgeAnchorProps {
  /**
   * Badge anchored to the top-right corner of the method button. The "last
   * used" pill lives here; `LastUsedAffordance` owns the pill and decides which
   * control gets one. This component owns only the anchor geometry, so passing
   * nothing renders the button alone.
   */
  badge?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Positions an optional badge at the top-right corner of a method button,
 * centered on its top border. The badge is absolutely positioned so it never
 * shifts the button's own box, letting the same anchor sit on Google, Apple,
 * or the email CTA without changing their layout.
 *
 * The badge straddles the border by half its own height rather than a fixed
 * offset, so a badge whose type or padding changes stays centered instead of
 * drifting off the line.
 */
export const MethodBadgeAnchor: React.FC<MethodBadgeAnchorProps> = ({ badge, children }) => (
  <Box className="relative w-full">
    {children}
    {badge ? (
      <Box
        className="absolute top-0 right-3 z-10 -translate-y-1/2"
        data-testid="method-badge-anchor-slot"
      >
        {badge}
      </Box>
    ) : null}
  </Box>
);
